import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { USDLoader } from "three/examples/jsm/loaders/USDLoader";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { useLoader } from "@react-three/fiber";
import { Center } from "@react-three/drei";
import { MeasurementAnnotations } from "./measurement-annotations";
import { HoleContours } from "./hole-contours";

const DEFAULT_COLOR = "#0089ce";
// the height every model is scaled to, in world units.
const VIEW_HEIGHT = 6;

// the file format of `url`, ignoring any presigned-URL query string.
function formatOf(url) {
  const path = url.split(/[?#]/)[0];
  return path.slice(path.lastIndexOf(".") + 1).toLowerCase();
}

// whether `url` is a bare mesh (the measured STL) rather than a textured scan.
export function isMeshOnlyModel(url) {
  return formatOf(url) === "stl";
}

// Loads the model at `url` and renders it centered and auto-scaled. Both
// USD/USDZ (the scanned model) and STL (the measured mesh) are supported.
// renderMode: "textureOn" | "textureOff" | "wireframe".
// sides: the measured feet of an STL mesh ([{ side, measurements }]), their
// sizes drawn as dimension lines.
// holes: the contours of the holes of an incomplete STL mesh (see
// HoleContours), outlined on it.
// onLoaded: called with `url` once its model is loaded, centered and in view.
export const Model = ({
  url,
  renderMode = "textureOn",
  sides,
  holes,
  onLoaded,
}) => {
  // auto-scale the centered model to a consistent size in view. note it first
  // undo the previous scaling if it exists. Centering happens once the model
  // is loaded and mounted, so this is also when it is on screen.
  const autoScale = useCallback(
    ({ container, height }) => {
      const unscaled = height / (container.scale.y || 1);
      container.scale.setScalar(VIEW_HEIGHT / unscaled);
      if (onLoaded) onLoaded(url);
    },
    [url, onLoaded],
  );

  const Loaded = isMeshOnlyModel(url) ? StlModel : UsdModel;

  return (
    <Center onCentered={autoScale}>
      <Loaded url={url} renderMode={renderMode} sides={sides} holes={holes} />
    </Center>
  );
};

Model.propTypes = {
  url: PropTypes.string.isRequired,
  renderMode: PropTypes.oneOf(["textureOn", "textureOff", "wireframe"]),
  sides: PropTypes.array,
  holes: PropTypes.array,
  onLoaded: PropTypes.func,
};

// the scanned model, textured.
const UsdModel = ({ url, renderMode }) => {
  const model = useLoader(USDLoader, url);

  const textureOffMaterial = useRef(
    new THREE.MeshLambertMaterial({
      color: DEFAULT_COLOR,
      side: THREE.DoubleSide,
    }),
  );

  useEffect(() => {
    model.traverse((child) => {
      if (!child.isMesh) return;

      // remember the original material on the mesh itself: the loaded model
      // is cached per URL across mounts, so a per-instance map could record
      // an already-replaced material as the "original".
      if (!child.userData.originalMaterial) {
        child.userData.originalMaterial = child.material;
      }
      const original = child.userData.originalMaterial;

      if (renderMode === "textureOff") {
        child.material = textureOffMaterial.current;
      } else {
        // restore the original material
        child.material = original;
        child.material.side = THREE.DoubleSide; // show both faces
        child.material.wireframe = renderMode === "wireframe";
      }
      child.material.needsUpdate = true;
    });
  }, [model, renderMode]);

  return <primitive object={model} />;
};

UsdModel.propTypes = Model.propTypes;

// the measured mesh — geometry only, so it is always shown in the flat color,
// annotated with the dimension lines of its measurements.
const StlModel = ({ url, renderMode, sides, holes }) => {
  const geometry = useLoader(STLLoader, url);
  const material = useMemo(
    () =>
      new THREE.MeshLambertMaterial({
        color: DEFAULT_COLOR,
        side: THREE.DoubleSide,
      }),
    [],
  );
  material.wireframe = renderMode === "wireframe";

  return (
    <group>
      <mesh geometry={geometry} material={material} />
      <MeasurementAnnotations sides={sides} geometry={geometry} />
      <HoleContours holes={holes} />
    </group>
  );
};

StlModel.propTypes = Model.propTypes;
